import api from '@/lib/api';
import type {
  CreateEmployeeProfileBlockFieldInput,
  EmployeeProfileBlockField,
  EmployeeProfileFieldValue,
  ReorderEmployeeProfileBlockFieldsInput,
  UpdateEmployeeProfileBlockFieldInput,
} from '@hygge/shared';
import { CACHE_TIME } from '@hygge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { employeeProfileBlocksKeys } from './use-employee-profile-blocks';

export const employeeProfileBlockFieldsKeys = {
  all: ['employee-profile-block-fields'] as const,
  byBlock: (blockId: string) =>
    [...employeeProfileBlockFieldsKeys.all, { blockId }] as const,
};

export const employeeProfileFieldValuesKeys = {
  all: ['employee-profile-field-values'] as const,
  byEmployee: (employeeId: string) =>
    [...employeeProfileFieldValuesKeys.all, { employeeId }] as const,
};

async function listFields(blockId: string): Promise<EmployeeProfileBlockField[]> {
  const { data } = await api.get<EmployeeProfileBlockField[]>(
    `/employee-profile-blocks/${blockId}/fields`,
  );
  return data;
}

async function createField(
  blockId: string,
  input: CreateEmployeeProfileBlockFieldInput,
): Promise<EmployeeProfileBlockField> {
  const { data } = await api.post<EmployeeProfileBlockField>(
    `/employee-profile-blocks/${blockId}/fields`,
    input,
  );
  return data;
}

async function updateField(
  blockId: string,
  fieldId: string,
  input: UpdateEmployeeProfileBlockFieldInput,
): Promise<EmployeeProfileBlockField> {
  const { data } = await api.patch<EmployeeProfileBlockField>(
    `/employee-profile-blocks/${blockId}/fields/${fieldId}`,
    input,
  );
  return data;
}

async function deleteField(blockId: string, fieldId: string): Promise<void> {
  await api.delete(`/employee-profile-blocks/${blockId}/fields/${fieldId}`);
}

async function reorderFields(
  blockId: string,
  input: ReorderEmployeeProfileBlockFieldsInput,
): Promise<EmployeeProfileBlockField[]> {
  const { data } = await api.post<EmployeeProfileBlockField[]>(
    `/employee-profile-blocks/${blockId}/fields/reorder`,
    input,
  );
  return data;
}

async function listValues(employeeId: string): Promise<EmployeeProfileFieldValue[]> {
  const { data } = await api.get<EmployeeProfileFieldValue[]>(
    `/employees/${employeeId}/custom-field-values`,
  );
  return data;
}

async function setValue(
  employeeId: string,
  fieldId: string,
  value: string,
): Promise<EmployeeProfileFieldValue> {
  const { data } = await api.put<EmployeeProfileFieldValue>(
    `/employees/${employeeId}/custom-field-values/${fieldId}`,
    { value },
  );
  return data;
}

async function clearValue(employeeId: string, fieldId: string): Promise<void> {
  await api.delete(`/employees/${employeeId}/custom-field-values/${fieldId}`);
}

async function uploadFileValue(
  employeeId: string,
  fieldId: string,
  file: File,
): Promise<EmployeeProfileFieldValue> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<EmployeeProfileFieldValue>(
    `/employees/${employeeId}/custom-field-values/${fieldId}/file`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export function useEmployeeProfileBlockFields(blockId: string) {
  return useQuery({
    queryKey: employeeProfileBlockFieldsKeys.byBlock(blockId),
    queryFn: () => listFields(blockId),
    enabled: !!blockId,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useCreateEmployeeProfileBlockField(blockId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEmployeeProfileBlockFieldInput) =>
      createField(blockId, input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeeProfileBlockFieldsKeys.byBlock(blockId),
      });
    },
  });
}

export function useUpdateEmployeeProfileBlockField(blockId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      fieldId,
      ...input
    }: UpdateEmployeeProfileBlockFieldInput & { fieldId: string }) =>
      updateField(blockId, fieldId, input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeeProfileBlockFieldsKeys.byBlock(blockId),
      });
    },
  });
}

export function useDeleteEmployeeProfileBlockField(blockId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fieldId: string) => deleteField(blockId, fieldId),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeeProfileBlockFieldsKeys.byBlock(blockId),
      });
      // Deleting a field cascades its stored values, which can flip the block's
      // hasStoredData flag — refresh the blocks list so Delete re-enables (HW-685).
      queryClient.invalidateQueries({ queryKey: employeeProfileBlocksKeys.all });
    },
  });
}

export function useReorderEmployeeProfileBlockFields(blockId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReorderEmployeeProfileBlockFieldsInput) =>
      reorderFields(blockId, input),
    // Rollback-only onError; call sites supply their own toast. Explicit flag for consistency.
    meta: { suppressGlobalToast: true },
    onMutate: async (input) => {
      await queryClient.cancelQueries({
        queryKey: employeeProfileBlockFieldsKeys.byBlock(blockId),
      });
      const previous = queryClient.getQueryData<EmployeeProfileBlockField[]>(
        employeeProfileBlockFieldsKeys.byBlock(blockId),
      );
      if (previous) {
        const lookup = new Map(previous.map((f) => [f.id, f]));
        const optimistic = input.fieldIds
          .map((id, index) => {
            const field = lookup.get(id);
            return field ? { ...field, position: index } : null;
          })
          .filter((f): f is EmployeeProfileBlockField => f !== null);
        queryClient.setQueryData(
          employeeProfileBlockFieldsKeys.byBlock(blockId),
          optimistic,
        );
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          employeeProfileBlockFieldsKeys.byBlock(blockId),
          context.previous,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: employeeProfileBlockFieldsKeys.byBlock(blockId),
      });
    },
  });
}

export function useEmployeeProfileFieldValues(employeeId: string) {
  return useQuery({
    queryKey: employeeProfileFieldValuesKeys.byEmployee(employeeId),
    queryFn: () => listValues(employeeId),
    enabled: !!employeeId,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useSetEmployeeProfileFieldValue(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldId, value }: { fieldId: string; value: string }) =>
      setValue(employeeId, fieldId, value),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeeProfileFieldValuesKeys.byEmployee(employeeId),
      });
      // Storing a value can flip a block's hasStoredData flag (HW-685).
      queryClient.invalidateQueries({ queryKey: employeeProfileBlocksKeys.all });
    },
  });
}

export function useClearEmployeeProfileFieldValue(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fieldId: string) => clearValue(employeeId, fieldId),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeeProfileFieldValuesKeys.byEmployee(employeeId),
      });
      // Clearing a value can flip a block's hasStoredData flag (HW-685).
      queryClient.invalidateQueries({ queryKey: employeeProfileBlocksKeys.all });
    },
  });
}

export function useUploadEmployeeProfileFieldFile(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fieldId, file }: { fieldId: string; file: File }) =>
      uploadFileValue(employeeId, fieldId, file),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeeProfileFieldValuesKeys.byEmployee(employeeId),
      });
      // Uploading a file value can flip a block's hasStoredData flag (HW-685).
      queryClient.invalidateQueries({ queryKey: employeeProfileBlocksKeys.all });
    },
  });
}
